import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "app-tag-selector__header" }
const _hoisted_2 = { class: "app-tag-selector__search" }
const _hoisted_3 = {
  key: 0,
  class: "app-tag-selector__body app-tag-selector__body--loading"
}
const _hoisted_4 = {
  key: 1,
  class: "app-tag-selector__body"
}
const _hoisted_5 = { class: "app-tag-selector__section-title" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "app-tag-selector__list" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  key: 1,
  class: "app-tag-selector__empty"
}
const _hoisted_10 = {
  key: 2,
  class: "app-tag-selector__body app-tag-selector__body--columns"
}
const _hoisted_11 = {
  key: 0,
  class: "app-tag-selector__columns"
}
const _hoisted_12 = {
  key: 0,
  class: "app-tag-selector__section app-tag-selector__section--system"
}
const _hoisted_13 = { class: "app-tag-selector__section-title" }
const _hoisted_14 = { class: "app-tag-selector__list" }
const _hoisted_15 = ["onClick"]
const _hoisted_16 = {
  key: 1,
  class: "app-tag-selector__section-right"
}
const _hoisted_17 = { class: "app-tag-selector__section-title" }
const _hoisted_18 = { class: "app-tag-selector__list" }
const _hoisted_19 = ["onClick"]
const _hoisted_20 = { class: "app-tag-selector__section-title" }
const _hoisted_21 = { class: "app-tag-selector__list" }
const _hoisted_22 = ["onClick"]
const _hoisted_23 = {
  key: 1,
  class: "app-tag-selector__empty"
}

import {
	ref,
	computed,
	watch,
	onMounted
} from 'vue';
import { debounce } from 'lodash';
import AppCheckbox from '@/components/ui-kit/checkbox/AppCheckbox.vue';
import AppInput from '@/components/ui-kit/input/AppInput.vue';
import AppIcon from '@/components/ui-kit/icon/AppIcon.vue';
import AppLoader from '@/components/common/AppLoader.vue';
import AppTooltip from '@/components/ui-kit/tooltip/AppTooltip.vue';
import type { TagWithFrequency, TagContainer } from '@/models/interfaces/tags/tag-models';
import type { TagCategory } from './types';
import { TagEntityType } from '@/models/etc/types';
import { TagsApi } from '@/ api/TagsApi';

type AppTagSelectorProps = {
	modelValue?: TagWithFrequency[];
	categories?: TagCategory[];
	entityType?: TagEntityType;
	placeholder?: string;
	mode?: 'filter' | 'select';
	allowCreate?: boolean;
	allowSelectGroup?: boolean;
};

type AppTagSelectorEmits = {
	'update:modelValue': [tags: TagWithFrequency[]];
	'reset': [];
	'close': [];
	'create-tag': [name: string];
};


export default /*@__PURE__*/_defineComponent({
  __name: 'AppTagSelector',
  props: {
    modelValue: { default: () => [] },
    categories: { default: () => [] },
    entityType: {},
    placeholder: { default: 'Поиск' },
    mode: { default: 'filter' },
    allowCreate: { type: Boolean, default: false },
    allowSelectGroup: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "reset", "close", "create-tag"],
  setup(__props: any, { emit: __emit }) {

const entityLabels: Record<number, string> = {
	[TagEntityType.Person]: 'Все теги контактов',
	[TagEntityType.Client]: 'Все теги клиентов',
	[TagEntityType.Project]: 'Все теги проектов',
	[TagEntityType.File]: 'Все теги файлов',
	[TagEntityType.Job]: 'Все теги работ',
};

const props = __props;

const emit = __emit;

const searchQuery = ref('');
const debouncedQuery = ref('');
const loading = ref(false);
const tagContainer = ref<TagContainer | null>(null);

const applySearchQuery = debounce((value: string) => {
	debouncedQuery.value = value;
}, 300);

watch(searchQuery, value => applySearchQuery(value));

const clearSearch = () => {
	applySearchQuery.cancel();
	searchQuery.value = '';
	debouncedQuery.value = '';
};

const loadTags = async () => {
	if (props.entityType == null) return;
	loading.value = true;
	try {
		const data = await TagsApi.getTagsList(props.entityType);
		tagContainer.value = data as TagContainer;
	} catch (e) {
		console.log(e);
	} finally {
		loading.value = false;
	}
};

const buildCategoriesFromContainer = (container: TagContainer): TagCategory[] => {
	const label = entityLabels[props.entityType!] || 'Все теги';
	if (props.mode === 'filter') {
		return [{ key: 'entity', label, tags: container.OftenUsedForEntityTags }];
	}
	return [
		{ key: 'system', label: 'Все теги системы', tags: container.AllSystemTags },
		{ key: 'entity', label, tags: container.OftenUsedForEntityTags },
		{ key: 'user', label: 'Все ваши теги', tags: container.OftenUsedByUserTags },
	];
};

const resolvedCategories = computed<TagCategory[]>(() => {
	if (props.entityType != null && tagContainer.value) {
		return buildCategoriesFromContainer(tagContainer.value);
	}
	return props.categories;
});

onMounted(() => loadTags());
watch(() => props.entityType, () => loadTags());

const rootClasses = computed(() => ({
	'app-tag-selector': true,
	'app-tag-selector--select': props.mode === 'select',
	'app-tag-selector--two-columns': hasTwoColumns.value,
}));

const selectedIds = computed(() => new Set(props.modelValue.map(t => t.Id)));

const hasSelection = computed(() => props.modelValue.length > 0);

const systemCategory = computed(() =>
	resolvedCategories.value.find(c => c.key === 'system')
);

const entityCategory = computed(() =>
	resolvedCategories.value.find(c => c.key === 'entity')
);

const userCategory = computed(() =>
	resolvedCategories.value.find(c => c.key === 'user')
);

const visibleCategories = computed(() =>
	resolvedCategories.value.filter(c => hasVisibleTags(c))
);

const matchesSearch = (tag: TagWithFrequency, query: string): boolean => {
	if (!query) return true;
	const lowerQuery = query.toLowerCase();
	const name = (tag.FullName || tag.Name).toLowerCase();
	return name.includes(lowerQuery);
};

const filteredByKey = computed<Record<string, TagWithFrequency[]>>(() => {
	const result: Record<string, TagWithFrequency[]> = {};
	for (const category of resolvedCategories.value) {
		result[category.key] = category.tags.filter(
			tag => tag.Frequency > 0 && matchesSearch(tag, debouncedQuery.value)
		);
	}
	return result;
});

const getFilteredTags = (category: TagCategory): TagWithFrequency[] => {
	return filteredByKey.value[category.key] ?? [];
};

const hasVisibleTags = (category: TagCategory): boolean => {
	return getFilteredTags(category).length > 0;
};

const hasTwoColumns = computed(() => {
	if (props.mode !== 'select') return false
	const hasLeft = !!(systemCategory.value && systemCategory.value.tags.some(t => t.Frequency > 0))
	const hasRight =
		!!(entityCategory.value && entityCategory.value.tags.some(t => t.Frequency > 0)) ||
		!!(userCategory.value && userCategory.value.tags.some(t => t.Frequency > 0))
	return hasLeft && hasRight
})

const hasAnyTags = computed(() => {
	return resolvedCategories.value.some(c => c.tags.some(t => t.Frequency > 0))
})

const formatTagLabel = (tag: TagWithFrequency): string => {
	return `${tag.Name.toLowerCase()} (${tag.Frequency})`;
};

const isTagSelected = (tagId: number): boolean => {
	return selectedIds.value.has(tagId);
};

const isCategoryAllSelected = (category: TagCategory): boolean => {
	const visibleTags = category.tags.filter(t => t.Frequency > 0);
	if (visibleTags.length === 0) return false;
	return visibleTags.every(t => selectedIds.value.has(t.Id));
};

const toggleTag = (tag: TagWithFrequency) => {
	const current = [...props.modelValue];
	const index = current.findIndex(t => t.Id === tag.Id);
	if (index >= 0) {
		current.splice(index, 1);
	} else {
		current.push(tag);
	}
	emit('update:modelValue', current);
};

const toggleCategory = (category: TagCategory) => {
	const visibleTags = category.tags.filter(t => t.Frequency > 0);
	const allSelected = isCategoryAllSelected(category);
	let current = [...props.modelValue];

	if (allSelected) {
		const categoryIds = new Set(visibleTags.map(t => t.Id));
		current = current.filter(t => !categoryIds.has(t.Id));
	} else {
		const existingIds = new Set(current.map(t => t.Id));
		for (const tag of visibleTags) {
			if (!existingIds.has(tag.Id)) {
				current.push(tag);
			}
		}
	}

	emit('update:modelValue', current);
};

const resetSelection = () => {
	emit('update:modelValue', []);
	emit('reset');
};

const createTag = () => {
	const name = searchQuery.value.trim();
	if (!name) {
		return;
	}
	emit('create-tag', name.toLowerCase());

	clearSearch();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(rootClasses.value)
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(AppInput, {
          modelValue: searchQuery.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
          placeholder: _ctx.placeholder,
          "show-clear-button": "",
          onClearField: clearSearch
        }, {
          "custom-icons": _withCtx(() => [
            (props.allowCreate && props.mode === 'select' && searchQuery.value)
              ? (_openBlock(), _createBlock(AppTooltip, {
                  key: 0,
                  text: "Добавить тег",
                  position: "top"
                }, {
                  activator: _withCtx(() => [
                    _createVNode(AppIcon, {
                      name: "plus",
                      width: "16px",
                      color: "#979797",
                      "hover-color": "#499259",
                      cursor: "pointer",
                      onClick: createTag
                    })
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["modelValue", "placeholder"]),
        _createElementVNode("div", {
          class: "app-tag-selector__close",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('close')))
        }, [
          _createVNode(AppIcon, {
            class: "app-tag-selector__close-icon",
            name: "close",
            width: "20px",
            color: "#979797",
            "hover-color": "#499259",
            cursor: "pointer"
          })
        ])
      ])
    ]),
    _withDirectives(_createElementVNode("span", {
      class: "app-tag-selector__reset",
      onClick: resetSelection
    }, " Сбросить ", 512), [
      [_vShow, !searchQuery.value && hasSelection.value]
    ]),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(AppLoader)
        ]))
      : (_ctx.mode === 'filter')
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            (visibleCategories.value.length > 0)
              ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(visibleCategories.value, (category) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: category.key,
                    class: "app-tag-selector__section"
                  }, [
                    _createElementVNode("div", _hoisted_5, [
                      (_ctx.allowSelectGroup)
                        ? (_openBlock(), _createBlock(AppCheckbox, {
                            key: 0,
                            style: { height: '16px', marginRight: '8px' },
                            value: isCategoryAllSelected(category),
                            text: "",
                            onOnInput: ($event: any) => (toggleCategory(category))
                          }, null, 8, ["value", "onOnInput"]))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", {
                        class: "app-tag-selector__section-title-text",
                        onClick: ($event: any) => (_ctx.allowSelectGroup ? toggleCategory(category) : undefined)
                      }, _toDisplayString(category.label), 9, _hoisted_6)
                    ]),
                    _createElementVNode("div", _hoisted_7, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getFilteredTags(category), (tag) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: tag.Id,
                          class: "app-tag-selector__tag"
                        }, [
                          _createVNode(AppCheckbox, {
                            style: { height: '16px', marginRight: '8px' },
                            value: isTagSelected(tag.Id),
                            text: "",
                            onOnInput: ($event: any) => (toggleTag(tag))
                          }, null, 8, ["value", "onOnInput"]),
                          _createElementVNode("div", {
                            class: "app-tag-selector__tag-text",
                            onClick: ($event: any) => (toggleTag(tag))
                          }, _toDisplayString(formatTagLabel(tag)), 9, _hoisted_8)
                        ]))
                      }), 128))
                    ])
                  ]))
                }), 128))
              : (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(hasAnyTags.value ? 'Тег не найден' : 'Теги отсутствуют'), 1))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_10, [
            (visibleCategories.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  (systemCategory.value && hasVisibleTags(systemCategory.value))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                        _createElementVNode("div", _hoisted_13, [
                          (_ctx.allowSelectGroup)
                            ? (_openBlock(), _createBlock(AppCheckbox, {
                                key: 0,
                                style: { height: '16px', marginRight: '8px' },
                                value: isCategoryAllSelected(systemCategory.value),
                                text: "",
                                onOnInput: _cache[2] || (_cache[2] = ($event: any) => (toggleCategory(systemCategory.value)))
                              }, null, 8, ["value"]))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", {
                            class: "app-tag-selector__section-title-text",
                            onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.allowSelectGroup ? toggleCategory(systemCategory.value) : undefined))
                          }, _toDisplayString(systemCategory.value.label), 1)
                        ]),
                        _createElementVNode("div", _hoisted_14, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getFilteredTags(systemCategory.value), (tag) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: 'sys-' + tag.Id,
                              class: "app-tag-selector__tag"
                            }, [
                              _createVNode(AppCheckbox, {
                                style: { height: '16px', marginRight: '8px' },
                                value: isTagSelected(tag.Id),
                                text: "",
                                onOnInput: ($event: any) => (toggleTag(tag))
                              }, null, 8, ["value", "onOnInput"]),
                              _createElementVNode("div", {
                                class: "app-tag-selector__tag-text",
                                onClick: ($event: any) => (toggleTag(tag))
                              }, _toDisplayString(formatTagLabel(tag)), 9, _hoisted_15)
                            ]))
                          }), 128))
                        ])
                      ]))
                    : _createCommentVNode("", true),
                  ((entityCategory.value && hasVisibleTags(entityCategory.value)) || (userCategory.value && hasVisibleTags(userCategory.value)))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                        (entityCategory.value && hasVisibleTags(entityCategory.value))
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 0,
                              class: _normalizeClass({
								'app-tag-selector__section': true,
								'app-tag-selector__section--entity': true,
								'app-tag-selector__section--full': !userCategory.value || !hasVisibleTags(userCategory.value)
							})
                            }, [
                              _createElementVNode("div", _hoisted_17, [
                                (_ctx.allowSelectGroup)
                                  ? (_openBlock(), _createBlock(AppCheckbox, {
                                      key: 0,
                                      style: { height: '16px', marginRight: '8px' },
                                      value: isCategoryAllSelected(entityCategory.value),
                                      text: "",
                                      onOnInput: _cache[4] || (_cache[4] = ($event: any) => (toggleCategory(entityCategory.value)))
                                    }, null, 8, ["value"]))
                                  : _createCommentVNode("", true),
                                _createElementVNode("div", {
                                  class: "app-tag-selector__section-title-text",
                                  onClick: _cache[5] || (_cache[5] = ($event: any) => (_ctx.allowSelectGroup ? toggleCategory(entityCategory.value) : undefined))
                                }, _toDisplayString(entityCategory.value.label), 1)
                              ]),
                              _createElementVNode("div", _hoisted_18, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getFilteredTags(entityCategory.value), (tag) => {
                                  return (_openBlock(), _createElementBlock("div", {
                                    key: 'ent-' + tag.Id,
                                    class: "app-tag-selector__tag"
                                  }, [
                                    _createVNode(AppCheckbox, {
                                      style: { height: '16px', marginRight: '8px' },
                                      value: isTagSelected(tag.Id),
                                      text: "",
                                      onOnInput: ($event: any) => (toggleTag(tag))
                                    }, null, 8, ["value", "onOnInput"]),
                                    _createElementVNode("div", {
                                      class: "app-tag-selector__tag-text",
                                      onClick: ($event: any) => (toggleTag(tag))
                                    }, _toDisplayString(formatTagLabel(tag)), 9, _hoisted_19)
                                  ]))
                                }), 128))
                              ])
                            ], 2))
                          : _createCommentVNode("", true),
                        (userCategory.value && hasVisibleTags(userCategory.value))
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 1,
                              class: _normalizeClass({
								'app-tag-selector__section': true,
								'app-tag-selector__section--user': true,
								'app-tag-selector__section--full': !entityCategory.value || !hasVisibleTags(entityCategory.value)
							})
                            }, [
                              _createElementVNode("div", _hoisted_20, [
                                (_ctx.allowSelectGroup)
                                  ? (_openBlock(), _createBlock(AppCheckbox, {
                                      key: 0,
                                      style: { height: '16px', marginRight: '8px' },
                                      value: isCategoryAllSelected(userCategory.value),
                                      text: "",
                                      onOnInput: _cache[6] || (_cache[6] = ($event: any) => (toggleCategory(userCategory.value)))
                                    }, null, 8, ["value"]))
                                  : _createCommentVNode("", true),
                                _createElementVNode("div", {
                                  class: "app-tag-selector__section-title-text",
                                  onClick: _cache[7] || (_cache[7] = ($event: any) => (_ctx.allowSelectGroup ? toggleCategory(userCategory.value) : undefined))
                                }, _toDisplayString(userCategory.value.label), 1)
                              ]),
                              _createElementVNode("div", _hoisted_21, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getFilteredTags(userCategory.value), (tag) => {
                                  return (_openBlock(), _createElementBlock("div", {
                                    key: 'usr-' + tag.Id,
                                    class: "app-tag-selector__tag"
                                  }, [
                                    _createVNode(AppCheckbox, {
                                      style: { height: '16px', marginRight: '8px' },
                                      value: isTagSelected(tag.Id),
                                      text: "",
                                      onOnInput: ($event: any) => (toggleTag(tag))
                                    }, null, 8, ["value", "onOnInput"]),
                                    _createElementVNode("div", {
                                      class: "app-tag-selector__tag-text",
                                      onClick: ($event: any) => (toggleTag(tag))
                                    }, _toDisplayString(formatTagLabel(tag)), 9, _hoisted_22)
                                  ]))
                                }), 128))
                              ])
                            ], 2))
                          : _createCommentVNode("", true)
                      ]))
                    : _createCommentVNode("", true)
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_23, _toDisplayString(hasAnyTags.value ? 'Тег не найден' : 'Теги отсутствуют'), 1))
          ]))
  ], 2))
}
}

})